import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  MultiSelect,
  Switch,
  Paper,
  TextInput,
  NumberInput,
  Select,
  SimpleGrid,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useExternalInstitutionsAdmin,
  useCreateExternalInstitution,
  useUpdateExternalInstitution,
  useToggleExternalInstitutionActive,
} from "../../../hooks/admin/useExternalInstitutions";
import { ExternalInstitution } from "../../../services/admin/externalInstitutions.service";
import { useStates } from "../../../hooks/useReferenceData";
import {
  required,
  email,
  phone,
  fax,
  zipCode,
  scrollToFirstError,
  showValidationNotification,
} from "../../../utils/formValidation";

interface FormValues {
  name: string;
  shortName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  fax: string;
  email: string;
  contactName: string;
  specialties: string;
  averageTurnaroundDays: number | "";
  isActive: boolean;
}

interface ExternalInstitutionFilters {
  states?: string[];
  activeStatus?: string[];
}

const ITEMS_PER_PAGE = 20;

export default function ExternalInstitutionsAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<ExternalInstitution | null>(null);
  const [itemToToggle, setItemToToggle] = useState<ExternalInstitution | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<ExternalInstitutionFilters>({});
  const [pendingFilters, setPendingFilters] =
    useState<ExternalInstitutionFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const {
    data: institutions = [],
    isLoading,
    refetch,
  } = useExternalInstitutionsAdmin();
  const createInstitution = useCreateExternalInstitution();
  const updateInstitution = useUpdateExternalInstitution();
  const toggleActive = useToggleExternalInstitutionActive();
  const { data: usStates = [] } = useStates();

  // State options from API - sorted alphabetically
  const stateSelectOptions = useMemo(() => {
    return usStates
      .map((s) => ({ value: s.code, label: `${s.code} - ${s.name}` }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [usStates]);

  const form = useForm<FormValues>({
    initialValues: {
      name: "",
      shortName: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      phone: "",
      fax: "",
      email: "",
      contactName: "",
      specialties: "",
      averageTurnaroundDays: "",
      isActive: true,
    },
    validate: {
      name: required("Institution name is required"),
      email: email(),
      phone: phone(),
      fax: fax(),
      zipCode: zipCode(),
    },
  });

  // Client-side filtering
  const filteredData = useMemo(() => {
    let data = institutions;

    // Text search
    if (debouncedSearch) {
      const lower = debouncedSearch.toLowerCase();
      data = data.filter(
        (inst) =>
          inst.name.toLowerCase().includes(lower) ||
          inst.shortName?.toLowerCase().includes(lower) ||
          inst.city?.toLowerCase().includes(lower) ||
          inst.state?.toLowerCase().includes(lower) ||
          inst.contactName?.toLowerCase().includes(lower) ||
          inst.specialties?.toLowerCase().includes(lower)
      );
    }

    // State filter
    if (filters.states && filters.states.length > 0) {
      data = data.filter(
        (inst) => inst.state && filters.states!.includes(inst.state)
      );
    }

    // Active status filter
    if (filters.activeStatus && filters.activeStatus.length > 0) {
      data = data.filter((inst) => {
        const status = inst.isActive ? "active" : "inactive";
        return filters.activeStatus!.includes(status);
      });
    }

    return data;
  }, [institutions, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  // Check if any filters are active
  const hasActiveFilters =
    (filters.states && filters.states.length > 0) ||
    (filters.activeStatus && filters.activeStatus.length > 0);

  // Unique states from data for filter options
  const stateOptions = useMemo(() => {
    const states = new Set(
      institutions.map((inst) => inst.state).filter(Boolean) as string[]
    );
    return Array.from(states)
      .sort((a, b) => a.localeCompare(b))
      .map((s) => ({ value: s, label: s }));
  }, [institutions]);

  const activeStatusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Handlers
  const handleAdd = () => {
    setEditing(null);
    form.reset();
    openFormModal();
  };

  const handleEdit = (item: ExternalInstitution) => {
    setEditing(item);
    form.setValues({
      name: item.name || "",
      shortName: item.shortName || "",
      address: item.address || "",
      city: item.city || "",
      state: item.state || "",
      zipCode: item.zipCode || "",
      phone: item.phone || "",
      fax: item.fax || "",
      email: item.email || "",
      contactName: item.contactName || "",
      specialties: item.specialties || "",
      averageTurnaroundDays: item.averageTurnaroundDays ?? "",
      isActive: item.isActive,
    });
    openFormModal();
  };

  const handleSubmit = async (values: FormValues) => {
    const payload = {
      ...values,
      averageTurnaroundDays:
        values.averageTurnaroundDays === ""
          ? undefined
          : values.averageTurnaroundDays,
    };

    try {
      if (editing) {
        await updateInstitution.mutateAsync({
          id: editing.id,
          ...payload,
        });
        notifications.show({
          title: "Institution updated",
          message: `"${values.name}" has been updated successfully.`,
          color: "green",
        });
      } else {
        await createInstitution.mutateAsync(payload);
        notifications.show({
          title: "Institution created",
          message: `"${values.name}" has been added successfully.`,
          color: "green",
        });
      }
      closeFormModal();
      form.reset();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || "An error occurred";
      showValidationNotification(message);
    }
  };

  const handleToggleActive = (item: ExternalInstitution) => {
    setItemToToggle(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (itemToToggle) {
      try {
        await toggleActive.mutateAsync({
          id: itemToToggle.id,
          isActive: !itemToToggle.isActive,
        });
        notifications.show({
          title: itemToToggle.isActive
            ? "Institution deactivated"
            : "Institution activated",
          message: `"${itemToToggle.name}" has been ${
            itemToToggle.isActive ? "deactivated" : "activated"
          }.`,
          color: itemToToggle.isActive ? "orange" : "green",
        });
      } catch (error: any) {
        showValidationNotification(
          error?.message || "Failed to update institution status"
        );
      }
      closeConfirmModal();
      setItemToToggle(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  const formatLocation = (inst: ExternalInstitution) => {
    const parts = [inst.city, inst.state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>External Institutions</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage external institutions for send-outs and consultations
                (e.g., Mayo Clinic, Moffitt Cancer Center)
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name, city, state, contact, or specialties..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.states && filters.states.length > 0 && (
                      <Badge variant="light" color="blue">
                        {filters.states.length} State
                        {filters.states.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {filters.activeStatus &&
                      filters.activeStatus.length > 0 && (
                        <Badge variant="light" color="green">
                          {filters.activeStatus.length} Status
                        </Badge>
                      )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} institution
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  External Institutions ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No institutions match the current filters"
                    : "No external institutions found. Add one to get started."}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Institution Name</Table.Th>
                      <Table.Th>Short Name</Table.Th>
                      <Table.Th>Location</Table.Th>
                      <Table.Th>Contact</Table.Th>
                      <Table.Th>Phone</Table.Th>
                      <Table.Th style={{ width: 100 }}>Avg. TAT</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        {/* Institution Name */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name}
                          </Text>
                          {item.isPreconfigured && (
                            <Badge
                              color="gray"
                              variant="light"
                              size="xs"
                              mt={2}
                            >
                              Preconfigured
                            </Badge>
                          )}
                          {item.specialties && (
                            <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
                              {item.specialties}
                            </Text>
                          )}
                        </Table.Td>

                        {/* Short Name */}
                        <Table.Td>
                          <Text size="sm">
                            {item.shortName || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>

                        {/* Location */}
                        <Table.Td>
                          <Text size="sm">
                            {formatLocation(item) || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>

                        {/* Contact */}
                        <Table.Td>
                          <Text size="sm">
                            {item.contactName || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>

                        {/* Phone */}
                        <Table.Td>
                          <Text size="sm">
                            {item.phone || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>

                        {/* Avg TAT */}
                        <Table.Td>
                          {item.averageTurnaroundDays != null ? (
                            <Badge color="blue" variant="light">
                              {item.averageTurnaroundDays}d
                            </Badge>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>

                        {/* Status */}
                        <Table.Td>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={
            editing ? "Edit External Institution" : "Add External Institution"
          }
          size="lg"
          closeOnClickOutside={false}
        >
          <form
            onSubmit={form.onSubmit(handleSubmit, () =>
              scrollToFirstError(form)
            )}
          >
            <Stack gap="md">
              <SimpleGrid cols={2}>
                <TextInput
                  label="Institution Name"
                  placeholder="e.g., Mayo Clinic"
                  withAsterisk
                  {...form.getInputProps("name")}
                />
                <TextInput
                  label="Short Name"
                  placeholder="e.g., Mayo - Jax"
                  {...form.getInputProps("shortName")}
                />
              </SimpleGrid>

              <TextInput
                label="Address"
                placeholder="Street address"
                {...form.getInputProps("address")}
              />

              <SimpleGrid cols={3}>
                <TextInput
                  label="City"
                  placeholder="e.g., Jacksonville"
                  {...form.getInputProps("city")}
                />
                <Select
                  label="State"
                  placeholder="Select state"
                  data={stateSelectOptions}
                  searchable
                  clearable
                  {...form.getInputProps("state")}
                />
                <TextInput
                  label="ZIP Code"
                  placeholder="e.g., 32224"
                  {...form.getInputProps("zipCode")}
                />
              </SimpleGrid>

              <SimpleGrid cols={2}>
                <TextInput
                  label="Phone"
                  placeholder="e.g., (904) 953-2000"
                  {...form.getInputProps("phone")}
                />
                <TextInput
                  label="Fax"
                  placeholder="e.g., (904) 953-2001"
                  {...form.getInputProps("fax")}
                />
              </SimpleGrid>

              <SimpleGrid cols={2}>
                <TextInput
                  label="Email"
                  type="email"
                  placeholder="pathology@institution.org"
                  {...form.getInputProps("email")}
                />
                <TextInput
                  label="Contact Name"
                  placeholder="e.g., Dr. Jane Smith"
                  {...form.getInputProps("contactName")}
                />
              </SimpleGrid>

              <SimpleGrid cols={2}>
                <TextInput
                  label="Specialties"
                  placeholder="e.g., Oncology, Dermatopathology"
                  {...form.getInputProps("specialties")}
                />
                <NumberInput
                  label="Avg. Turnaround (days)"
                  placeholder="e.g., 14"
                  min={1}
                  max={365}
                  {...form.getInputProps("averageTurnaroundDays")}
                />
              </SimpleGrid>

              <Switch
                label="Active"
                description="Inactive institutions won't appear in send-out destination lists"
                {...form.getInputProps("isActive", { type: "checkbox" })}
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={closeFormModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={
                    createInstitution.isPending || updateInstitution.isPending
                  }
                >
                  {editing ? "Update" : "Create"}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter External Institutions"
          size="md"
        >
          <Stack gap="md">
            <MultiSelect
              label="States"
              placeholder="Select states..."
              data={stateOptions}
              value={pendingFilters.states || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  states: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />

            <MultiSelect
              label="Status"
              placeholder="Select status..."
              data={activeStatusOptions}
              value={pendingFilters.activeStatus || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  activeStatus: values.length > 0 ? values : undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            itemToToggle?.isActive
              ? "Deactivate Institution"
              : "Activate Institution"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"}{" "}
              <strong>{itemToToggle?.name}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated institutions will not appear in send-out destination
                lists.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
